import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { shopApi } from '../api/shop';
import { useAuth } from '../auth/AuthProvider';

const ShopContext = createContext(null);
const STORAGE_KEY = 'salesbotics.selectedShopId';

export function ShopProvider({ children }) {
  const { getToken } = useAuth();
  const [shops, setShops] = useState([]);
  const [isPlatformUser, setIsPlatformUser] = useState(false);
  const [selectedShopId, setSelectedShopId] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadShops = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const me = await shopApi.getMe(token);
      const platformUser = Boolean(me.platform_role);
      setIsPlatformUser(platformUser);

      if (platformUser) {
        setShops([]);
        setSelectedShopId(null);
        return;
      }

      const data = await shopApi.listMyShops(token);
      setShops(data);
      setSelectedShopId((current) => {
        if (data.length === 1) {
          return data[0].shop.id;
        }
        if (current && data.some((entry) => entry.shop.id === current)) {
          return current;
        }
        return data[0]?.shop.id ?? null;
      });
    } catch (err) {
      setError(err.message);
      setIsPlatformUser(false);
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadShops();
  }, [loadShops]);

  useEffect(() => {
    if (selectedShopId != null) {
      localStorage.setItem(STORAGE_KEY, String(selectedShopId));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedShopId]);

  const selectedShop = useMemo(
    () => shops.find((entry) => entry.shop.id === selectedShopId) ?? null,
    [shops, selectedShopId],
  );

  const value = useMemo(
    () => ({
      shops,
      selectedShop,
      selectedShopId,
      setSelectedShopId,
      isPlatformUser,
      loading,
      error,
      reloadShops: loadShops,
    }),
    [shops, selectedShop, selectedShopId, isPlatformUser, loading, error, loadShops],
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within ShopProvider');
  }
  return context;
}
